import { useMemo, useState } from "react"
import { useParams } from "react-router"
import { useNavigate } from "react-router-dom"
import { observer } from "mobx-react"
import { Button, Flex, List } from "antd"
import { CopyOutlined } from "@ant-design/icons"
import Tippy from "@tippyjs/react"
import classNames from "classnames"
import path from "path"

import { Navigation } from "@src/app/components/navigation"
import { notify } from "@src/app/components/notify"
import { configStore } from "@src/app/configStore"
import { DefaultPageLayout } from "@src/app/layouts/DefaultPageLayout"
import { paths } from "@src/app/paths"
import { dateTimeFormat } from "@src/shared/date"
import { paddingLimit } from "@src/shared/strings"
import css from "./index.module.css"
import { PaymentLinkStore } from "./store"

const { Item } = List

export const PaymentLinkPage = observer(() => {
  const { id } = useParams()

  const [store] = useState(new PaymentLinkStore(id!))
  const nav = useNavigate()
  const { isLoading, paymentLink } = store

  const {
    config: { payment_page_url },
  } = configStore

  const {
    created_at,
    customer_email,
    customer_name,
    fiat_price,
    memo,
    status,
    updated_at,
  } = paymentLink || {}

  const invoiceUrl = `${payment_page_url}/invoice/${id}`

  const shouldViewApprove = useMemo(() => {
    return status === "unresolved"
  }, [status])

  const copyShareLink = () => {
    navigator.clipboard.writeText(invoiceUrl)

    notify.success("Copied")
  }

  const resolve = () => {
    store.resolvePayment().then(() => nav(paths.paymentLinks))
  }

  return (
    <DefaultPageLayout title="Payment link details">
      <Flex align={"center"} gap={16} vertical>
        <List className={css.list} loading={isLoading}>
          <Item>
            <div>Customer Name</div>
            <div className={css.listValue}>{customer_name}</div>
          </Item>
          <Item>
            <div>Customer Email</div>
            <div className={css.listValue}>{customer_email}</div>
          </Item>
          <Item>
            <div>Memo</div>
            <div className={css.listValue}>{memo}</div>
          </Item>
          <Item>
            <div>Amount requested</div>
            <div
              className={css.listValue}
            >{`${fiat_price?.amount} (${fiat_price?.symbol})`}</div>
          </Item>
          <Item>
            <div>Status</div>
            <div className={css.listValue}>
              {status === "void" ? "voided" : status}
            </div>
          </Item>
          <Item>
            <div>Created</div>
            <div className={css.listValue}>
              {created_at && dateTimeFormat(created_at)}
            </div>
          </Item>
          <Item>
            <div>Updated</div>
            <div className={css.listValue}>
              {updated_at && dateTimeFormat(updated_at)}
            </div>
          </Item>
          <Item>
            <div>Share a link</div>
            <div className={classNames(css.linkWrapper, css.listValue)}>
              <div>{paddingLimit(invoiceUrl, 0, 20)}</div>
              <Tippy className={css.tooltip} content="Copy to clipboard">
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => copyShareLink()}
                />
              </Tippy>
            </div>
          </Item>
        </List>

        {shouldViewApprove && (
          <Button
            id="GENERATED_ID_PAYMENYLINK_BUTTON_APPROVE_1"
            onClick={() => resolve()}
            size="large"
            type="default"
          >
            Approve
          </Button>
        )}
      </Flex>
    </DefaultPageLayout>
  )
})
